import React, { Component } from 'react';
import { connect } from 'react-redux';
import HistogramComponent from './HistogramComponent';
import selectors from '../../../store/appSelectors';
import './Histogram.scss';

const MIN_HEIGHT_PX = 80;
const MAX_HEIGHT_PX = 340;
const DEFAULT_HEIGHT_PX = 140;
// Collapsed handle bar height (px) — just the toggle strip
const HANDLE_HEIGHT_PX = 28;

class HistogramPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      heightPx: DEFAULT_HEIGHT_PX,
      dragging: false
    };
    this._dragStartY = null;
    this._dragStartHeight = null;
    this._rafId = null;
    this._pendingHeight = null;
    this.panelRef = React.createRef();
    this._onMouseMove = this._onMouseMove.bind(this);
    this._onMouseUp = this._onMouseUp.bind(this);
    this._onTouchMove = this._onTouchMove.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);
  }

  componentDidMount() {
    this._notifyHeightChange();
  }

  componentDidUpdate(prevProps, prevState) {
    const prevHeight = prevState.open ? prevState.heightPx : 0;
    const nextHeight = this.state.open ? this.state.heightPx : 0;
    if (prevHeight !== nextHeight) {
      this._notifyHeightChange();
    }
  }

  componentWillUnmount() {
    this._removeDragListeners();
    if (this._rafId) cancelAnimationFrame(this._rafId);
  }

  _notifyHeightChange() {
    if (!this.props.onHeightChange) return;
    const panelHeight = this.state.open ? this.state.heightPx : 0;
    this.props.onHeightChange(HANDLE_HEIGHT_PX + panelHeight);
  }

  _addDragListeners() {
    window.addEventListener('mousemove', this._onMouseMove);
    window.addEventListener('mouseup', this._onMouseUp);
    window.addEventListener('touchmove', this._onTouchMove, { passive: false });
    window.addEventListener('touchend', this._onTouchEnd);
  }

  _removeDragListeners() {
    window.removeEventListener('mousemove', this._onMouseMove);
    window.removeEventListener('mouseup', this._onMouseUp);
    window.removeEventListener('touchmove', this._onTouchMove);
    window.removeEventListener('touchend', this._onTouchEnd);
  }

  _startDrag(clientY) {
    this._dragStartY = clientY;
    this._dragStartHeight = this.state.heightPx;
    this._addDragListeners();
    this.setState({ dragging: true });
    if (this.panelRef.current) {
      this.panelRef.current.style.transition = 'none';
    }
  }

  _updateHeight(clientY) {
    // Dragging the handle upward (smaller clientY) → increases panel height
    const delta = this._dragStartY - clientY;
    this._pendingHeight = Math.min(
      MAX_HEIGHT_PX,
      Math.max(MIN_HEIGHT_PX, this._dragStartHeight + delta)
    );
    if (!this._rafId) {
      this._rafId = requestAnimationFrame(() => {
        this._rafId = null;
        if (this._pendingHeight !== null) {
          this.setState({ heightPx: this._pendingHeight });
          this._pendingHeight = null;
        }
      });
    }
  }

  _endDrag() {
    this._removeDragListeners();
    this.setState({ dragging: false });
    if (this.panelRef.current) {
      this.panelRef.current.style.transition = '';
    }
  }

  _onMouseMove(e) {
    if (!this.state.dragging) return;
    this._updateHeight(e.clientY);
  }

  _onMouseUp() {
    if (this.state.dragging) this._endDrag();
  }

  _onTouchMove(e) {
    if (!this.state.dragging) return;
    e.preventDefault();
    this._updateHeight(e.touches[0].clientY);
  }

  _onTouchEnd() {
    if (this.state.dragging) this._endDrag();
  }

  _handleDragStart(e) {
    // Prevent text selection during drag
    e.preventDefault();
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    // Open the panel automatically if collapsed when drag starts
    if (!this.state.open) {
      this.setState({ open: true }, () => this._startDrag(clientY));
    } else {
      this._startDrag(clientY);
    }
  }

  _toggle() {
    this.setState(prev => ({ open: !prev.open }));
  }

  render() {
    const { open, heightPx, dragging } = this.state;
    const { aggregatesFetching, isMobile } = this.props;

    // On mobile the histogram is hidden — too cramped
    if (isMobile) return null;

    const panelHeight = open ? heightPx : 0;

    const totalCount =
      this.props.aggregates &&
      (() => {
        const agg = this.props.aggregates;
        const keys = [
          'dateTime',
          'date',
          'observationDate',
          'datetime',
          'when'
        ];
        for (const k of keys) {
          if (agg[k] && agg[k].facetValues) {
            return agg[k].facetValues
              .reduce((s, v) => s + (Number(v.count) || 0), 0)
              .toLocaleString();
          }
        }
        return null;
      })();

    return (
      <div
        className={`histogram-panel${open ? ' histogram-panel--open' : ''}${
          dragging ? ' histogram-panel--dragging' : ''
        }`}
        ref={this.panelRef}
        style={{ flexShrink: 0 }}
      >
        {/* ── Drag handle + toggle ── */}
        <div
          className="histogram-handle"
          onMouseDown={e => this._handleDragStart(e)}
          onTouchStart={e => this._handleDragStart(e)}
          aria-hidden="true"
          style={{ cursor: dragging ? 'ns-resize' : undefined }}
        >
          <div className="histogram-handle__grip" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>

          <button
            className="histogram-handle__toggle"
            onClick={e => {
              e.stopPropagation();
              this._toggle();
            }}
            aria-expanded={open}
            aria-label={
              open
                ? 'Collapse observation histogram'
                : 'Expand observation histogram'
            }
            type="button"
          >
            <span className="histogram-handle__label">
              {aggregatesFetching
                ? 'Observation timeline…'
                : totalCount
                ? `Observation timeline · ${totalCount} total`
                : 'Observation timeline'}
            </span>
            <i
              className={`ti ${open ? 'ti-chevron-down' : 'ti-chevron-up'}`}
              aria-hidden="true"
            />
          </button>
        </div>

        {/* ── Chart body ── */}
        <div
          className="histogram-body"
          style={{ height: panelHeight }}
          aria-hidden={!open}
        >
          {open && <HistogramComponent />}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  aggregates: selectors.timeline.aggregates(state.timeline),
  aggregatesFetching: selectors.timeline.aggregatesFetching(state.timeline)
});

export default connect(mapStateToProps)(HistogramPanel);
